import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, TablePagination } from '@material-ui/core';
import { colors } from '@/styles';
import SearchInput from '@/components/misc/SearchInput';
import GridHeader from './GridHeader';
import FilterSelect from './FilterSelect';
import ColumnHeader from './ColumnHeader';
import ParentRow from './ParentRow';
import BulkActions from './BulkActions';
import { toBarButtons } from './buttons';
import { applyColumnOps, applySearch, columnKey, resolveChildFilterOptions, resolveFilterOptions } from './gridOps';
import {
    GridCard,
    Toolbar,
    LegendRow,
    LegendLabel,
    LegendItem,
    LegendDot,
    HeadRow,
    SelectCell,
    EmptyState,
    StickyBulkBar,
} from './blocks';

/**
 * Domain-agnostic expandable parent/child grid. It knows nothing about the domain it renders —
 * grouping, columns, status, actions, header fields and progress are all injected via config.
 *
 * Props:
 *   subject                 the entity the whole grid is about (e.g. a manifest); passed to header + button ctx
 *   rows                    parent models (e.g. orders)
 *   getRowId(row)           unique parent id
 *   getChildren(row)        child models for a parent (e.g. an order's items — one child per item)
 *   getChildId(child)       unique child id
 *   parentColumns           [{ key, header, width, render(row, { expanded, searchHint }) }] — each render returns ONE grid cell
 *   childColumns            [{ key, header, width, variant?, get(child, row) }] — variant: 'id' | 'pill' | text
 *   header                  optional { title(subject), subtitle?(subject), statusPill?(subject),
 *                                      fields:[{ label, get(subject), visible?(subject) }],
 *                                      actions:[{ key, icon, color, ariaLabel, visible?(subject), onClick(subject) }],
 *                                      progress?: bar | [bar] where bar = { label, verb, value(rows, subject), total(rows, subject) } }
 *   legend                  optional [{ label, color }]
 *   filter                  optional { label, options:[{ value, label }], classify(child) => value }
 *   search                  optional { placeholder, getOrderText(row), getChildText(child), initialTerm }
 *                                      initialTerm pre-fills the search box on mount (read once)
 *   bulkButtons             button configs for the sticky bar (persistent + selection)
 *   parentActions           button configs for the parent 3-dots
 *   rowActions              button configs for the child 3-dots
 *   selectable              show selection checkboxes
 *   selectionNoun           label used in "N items selected" (default 'item')
 *   childPanelLabel         optional divider label above the child table
 *   childFootnote           optional node beneath the child table
 *   columnOps               enable per-column sort/filter kebabs (columns opt in via sortable / filterOptions)
 *   resizable               enable drag-to-resize column widths (parent + child)
 *   paginated               paginate rows; default false = endless (render everything)
 *   pagination              optional { parent:{ size, options }, child:{ size, options } } — used only when paginated
 *   emptyText               shown when there are no rows at all
 *   emptyFilteredText       shown when rows exist but none match search/filter
 */
const ExpandableGrid = ({
    subject,
    rows = [],
    getRowId,
    getChildren,
    getChildId,
    parentColumns = [],
    childColumns = [],
    header = null,
    legend = null,
    filter = null,
    search = null,
    bulkButtons = [],
    parentActions = [],
    rowActions = [],
    selectable = false,
    selectionNoun = 'item',
    childPanelLabel = null,
    childFootnote = null,
    columnOps = false,
    resizable = false,
    paginated = false,
    pagination = null,
    emptyText = 'Nothing here yet.',
    emptyFilteredText = 'No results match your search or filters.',
}) => {
    const parentPageSize = pagination?.parent?.size ?? 10;
    const parentPageOptions = pagination?.parent?.options ?? [10, 25, 100];
    const childPageSize = pagination?.child?.size ?? 5;
    const childPageOptions = pagination?.child?.options ?? [5, 10, 25];

    const [searchTerm, setSearchTerm] = useState(search?.initialTerm ?? '');
    const [filterValues, setFilterValues] = useState([]);
    const [selected, setSelected] = useState({});
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(parentPageSize);

    // Per-column sort/filter state. Child ops are grid-global (consistent across expanded rows).
    const [parentSort, setParentSort] = useState(null);
    const [parentFilters, setParentFilters] = useState({});
    const [childSort, setChildSort] = useState(null);
    const [childFilters, setChildFilters] = useState({});

    // Drag-to-resize width overrides (px) by column key; empty = declared width.
    const [parentWidths, setParentWidths] = useState({});
    const [childWidths, setChildWidths] = useState({});
    const widthOf = (column, overrides) => {
        const px = overrides[columnKey(column)];
        return px != null ? `${px}px` : column.width;
    };

    const parentGridTemplate = `34px 22px ${parentColumns.map((c) => widthOf(c, parentWidths)).join(' ')} 34px`;
    const childGridTemplate = `34px ${childColumns.map((c) => widthOf(c, childWidths)).join(' ')} 34px`;

    const filterActive = !!(filter && filterValues.length);
    const effectiveChildren = useMemo(
        () => (row) => {
            let all = getChildren(row) || [];
            if (filterActive) all = all.filter((child) => filterValues.includes(filter.classify(child)));
            if (columnOps) all = applyColumnOps(all, childColumns, { sort: childSort, filters: childFilters }, row);
            return all;
        },
        [getChildren, filterActive, filterValues, filter, columnOps, childColumns, childSort, childFilters]
    );

    const term = (searchTerm || '').trim().toLowerCase();
    // Search runs on the column-filtered children. The status filter still hides a row whose
    // every child was removed, even when the order text matches.
    const searchedById = useMemo(() => {
        const map = new Map();
        rows.forEach((row) => {
            const kids = effectiveChildren(row);
            const result = search
                ? applySearch({
                      term: searchTerm,
                      row,
                      children: kids,
                      getOrderText: search.getOrderText,
                      getChildText: search.getChildText,
                  })
                : { include: true, children: kids, hint: null, autoExpand: false };
            map.set(getRowId(row), {
                ...result,
                include: result.include && !(filterActive && kids.length === 0),
            });
        });
        return map;
    }, [rows, effectiveChildren, search, searchTerm, filterActive, getRowId]);

    const visibleChildren = useMemo(
        () => (row) => searchedById.get(getRowId(row))?.children || [],
        [searchedById, getRowId]
    );

    const displayRows = useMemo(() => {
        const matched = rows.filter((row) => searchedById.get(getRowId(row))?.include);
        return columnOps
            ? applyColumnOps(matched, parentColumns, { sort: parentSort, filters: parentFilters })
            : matched;
    }, [rows, searchedById, getRowId, columnOps, parentColumns, parentSort, parentFilters]);

    // Resolved filter options per column (config, or derived from data).
    const parentFilterOptions = useMemo(
        () =>
            columnOps
                ? Object.fromEntries(parentColumns.map((c) => [columnKey(c), resolveFilterOptions(c, rows)]))
                : {},
        [columnOps, parentColumns, rows]
    );
    const childFilterOptions = useMemo(
        () =>
            columnOps
                ? Object.fromEntries(
                      childColumns.map((c) => [columnKey(c), resolveChildFilterOptions(c, rows, getChildren)])
                  )
                : {},
        [columnOps, childColumns, rows, getChildren]
    );

    // A different result set means the old page number is meaningless — start over.
    useEffect(() => setPage(0), [term, filterValues, parentSort, parentFilters]);

    // Any other shrink (a refetch, a smaller `rows`) must not leave us stranded past the end.
    const pageCount = Math.max(1, Math.ceil(displayRows.length / pageSize));
    const safePage = Math.min(page, pageCount - 1);
    useEffect(() => {
        if (page !== safePage) setPage(safePage);
    }, [page, safePage]);

    const pageRows = useMemo(
        () => (paginated ? displayRows.slice(safePage * pageSize, safePage * pageSize + pageSize) : displayRows),
        [displayRows, safePage, pageSize, paginated]
    );

    // Selection cascades by toggling child ids.
    const setMany = (children, value) =>
        setSelected((s) => {
            const next = { ...s };
            children.forEach((child) => {
                const id = getChildId(child);
                if (value) next[id] = true;
                else delete next[id];
            });
            return next;
        });
    const toggleChild = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
    const toggleRow = (row) => {
        const kids = visibleChildren(row);
        const all = kids.length > 0 && kids.every((child) => selected[getChildId(child)]);
        setMany(kids, !all);
    };
    const clearSelection = () => setSelected({});

    const allKept = useMemo(() => displayRows.flatMap(visibleChildren), [displayRows, visibleChildren]);
    const selectedChildren = useMemo(() => allKept.filter((child) => selected[getChildId(child)]), [allKept, selected]);

    const pageChildren = useMemo(() => pageRows.flatMap(visibleChildren), [pageRows, visibleChildren]);
    const allPageSelected = pageChildren.length > 0 && pageChildren.every((child) => selected[getChildId(child)]);
    const somePageSelected = pageChildren.some((child) => selected[getChildId(child)]) && !allPageSelected;
    const togglePageAll = () => setMany(pageChildren, !allPageSelected);

    // Resolve the header config against the subject.
    const resolvedHeader = useMemo(() => {
        if (!header) return null;
        return {
            title: header.title?.(subject),
            subtitle: header.subtitle?.(subject),
            statusPill: header.statusPill?.(subject) || null,
            fields: (header.fields || [])
                .filter((f) => (f.visible ? f.visible(subject) : true))
                .map((f) => ({ label: f.label, value: f.get(subject) })),
            actions: (header.actions || [])
                .filter((a) => (a.visible ? a.visible(subject) : true))
                .map((a) => ({
                    key: a.key,
                    icon: a.icon,
                    color: a.color,
                    ariaLabel: a.ariaLabel,
                    tooltip: a.tooltip,
                    onClick: () => a.onClick(subject),
                })),
            progress: header.progress
                ? (Array.isArray(header.progress) ? header.progress : [header.progress]).map((bar) => ({
                      label: bar.label,
                      verb: bar.verb,
                      value: bar.value(rows, subject),
                      total: bar.total(rows, subject),
                  }))
                : null,
        };
    }, [header, subject, rows]);

    const barButtons = toBarButtons(bulkButtons, { subject, selected: selectedChildren, clearSelection }).map(
        (btn) => ({
            ...btn,
            onClick: () => {
                btn.onClick();
                clearSelection();
            },
        })
    );

    const statusOptions = filter?.options || [];

    return (
        <>
            <GridCard>
                {resolvedHeader ? <GridHeader {...resolvedHeader} /> : null}

                {search || filter ? (
                    <Toolbar>
                        <div style={{ maxWidth: 360, width: '100%' }}>
                            {search ? (
                                <SearchInput
                                    placeholder={search.placeholder}
                                    onChange={setSearchTerm}
                                    initialValue={searchTerm}
                                />
                            ) : null}
                        </div>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            {filter ? (
                                <FilterSelect
                                    label={filter.label}
                                    options={statusOptions}
                                    value={filterValues}
                                    onChange={setFilterValues}
                                />
                            ) : null}
                        </div>
                    </Toolbar>
                ) : null}

                {legend?.length ? (
                    <LegendRow>
                        <LegendLabel>Status</LegendLabel>
                        {legend.map((entry) => (
                            <LegendItem key={entry.label}>
                                <LegendDot color={entry.color} />
                                {entry.label}
                            </LegendItem>
                        ))}
                    </LegendRow>
                ) : null}

                <HeadRow columns={parentGridTemplate}>
                    <SelectCell>
                        {selectable ? (
                            <Checkbox
                                size="small"
                                style={{ padding: 0, color: colors.greens.primary }}
                                checked={allPageSelected}
                                indeterminate={somePageSelected}
                                onChange={togglePageAll}
                            />
                        ) : null}
                    </SelectCell>
                    <span />
                    {parentColumns.map((column) => (
                        <ColumnHeader
                            key={columnKey(column)}
                            column={column}
                            columnOps={columnOps}
                            sortDir={parentSort?.key === columnKey(column) ? parentSort.dir : null}
                            onSort={(dir) => setParentSort(dir ? { key: columnKey(column), dir } : null)}
                            filterOptions={parentFilterOptions[columnKey(column)]}
                            filterValues={parentFilters[columnKey(column)] || []}
                            onFilter={(values) =>
                                setParentFilters((prev) => ({ ...prev, [columnKey(column)]: values }))
                            }
                            resizable={resizable}
                            onResize={(px) => setParentWidths((prev) => ({ ...prev, [columnKey(column)]: px }))}
                        />
                    ))}
                    <span />
                </HeadRow>

                {displayRows.length === 0 ? (
                    <EmptyState>{rows.length === 0 ? emptyText : emptyFilteredText}</EmptyState>
                ) : (
                    <>
                        {pageRows.map((row) => (
                            <ParentRow
                                key={getRowId(row)}
                                subject={subject}
                                row={row}
                                parentColumns={parentColumns}
                                parentGridTemplate={parentGridTemplate}
                                childColumns={childColumns}
                                childGridTemplate={childGridTemplate}
                                getChildren={visibleChildren}
                                autoExpand={!!searchedById.get(getRowId(row))?.autoExpand}
                                searchHint={searchedById.get(getRowId(row))?.hint || null}
                                searchTerm={searchTerm}
                                getChildId={getChildId}
                                selectable={selectable}
                                selected={selected}
                                onToggleChild={toggleChild}
                                onToggleRow={toggleRow}
                                parentActions={parentActions}
                                rowActions={rowActions}
                                childPanelLabel={childPanelLabel}
                                childFootnote={childFootnote}
                                columnOps={columnOps}
                                childSort={childSort}
                                setChildSort={setChildSort}
                                childFilters={childFilters}
                                setChildFilters={setChildFilters}
                                childFilterOptions={childFilterOptions}
                                resizable={resizable}
                                childWidths={childWidths}
                                setChildWidths={setChildWidths}
                                paginated={paginated}
                                childPageSize={childPageSize}
                                childPageOptions={childPageOptions}
                            />
                        ))}
                        {paginated ? (
                            <TablePagination
                                component="div"
                                count={displayRows.length}
                                page={safePage}
                                rowsPerPage={pageSize}
                                rowsPerPageOptions={parentPageOptions}
                                labelRowsPerPage="Rows per page"
                                onChangePage={(_, next) => setPage(next)}
                                onChangeRowsPerPage={(e) => {
                                    setPageSize(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                            />
                        ) : null}
                    </>
                )}
            </GridCard>

            {barButtons.length > 0 ? (
                <StickyBulkBar>
                    <BulkActions
                        count={selectedChildren.length}
                        noun={selectionNoun}
                        buttons={barButtons}
                        onClear={clearSelection}
                    />
                </StickyBulkBar>
            ) : null}
        </>
    );
};

export default ExpandableGrid;
